import React, { useEffect, useRef } from 'react'
import { LayoutBand, Skeleton, Card, colors, FlowGrid, usePageWidth, Bleed, Row, useStatusOverlay } from '@fs/zion-ui'
import { useEvent } from '@fs/zion-frontend-friends'
import { usePageView } from '@fs/zion-analytics'
import { Title } from '@fs/zion-head'
import { useTranslation } from 'react-i18next'
import { sortAndFilterGroups, useGroupsList } from '../services/getGroupsList'
import GroupLimitsProvider from '../providers/GroupLimitsProvider'
import GroupList from '../components/group-lists/GroupList'
import JoinGroupOverlay from '../components/join-group-overlay/JoinGroupOverlay'
import { useGedcomFileList } from '../services/getGedcomFileList'
import { useInterval } from '../helpers/utils'

export default function ResearchTreeListPage() {
  usePageView({ page_detail: 'View List: CET' }) // page_detail updates link_page for all analytics past this point
  const [{ data, error }, refetch] = useGroupsList()
  const isInitialLoading = !(error ?? data?.memberSummaries)
  const [{ data: gedcomFileList }, refetchGedcomFileList] = useGedcomFileList()
  const atPageWidth = usePageWidth()
  const isMobile = atPageWidth({ lg: false, default: true })
  const [t] = useTranslation()
  const hasProcessingGroup = useRef(false)

  const refetchGroupList = useEvent(() => Promise.all([refetch(true), refetchGedcomFileList()]))

  // When routing client-side back to this list, we need to call refetch to update the list
  useEffect(() => {
    refetchGroupList()
  }, [refetchGroupList])

  const sortedGroups = sortAndFilterGroups({
    groups: data?.memberSummaries || [],
    gedcomFileList,
  })

  // Polling for gedcoms being processed/uploaded/imported
  const processingGroups = sortedGroups.filter((group) => group.isProcessing)
  const interval = processingGroups.length > 0 ? 10000 : null
  const needToRefetchGroups = processingGroups.some((group) => group.needToRefetchGroups)
  const showStatusOverlay = useStatusOverlay()

  useEffect(() => {
    if (processingGroups.length === 0 && hasProcessingGroup.current) {
      showStatusOverlay({
        message: t('File successfully processed.'),
        type: 'success',
        dismissable: true,
      })
      hasProcessingGroup.current = false
    }
  }, [processingGroups.length, showStatusOverlay, t])

  useInterval(() => {
    if (needToRefetchGroups) {
      refetchGroupList()
    } else {
      refetchGedcomFileList()
    }
    hasProcessingGroup.current = true
  }, interval)

  if (isInitialLoading) return <ResearchTreeListSkeletons isMobile={isMobile} />
  return (
    <>
      <Title>{t('group-management.research-trees.heading')}</Title>
      <GroupLimitsProvider>
        <GroupList
          refetchGroupList={refetchGroupList}
          groups={sortedGroups}
          totalGroupCount={data?.memberSummaries?.length}
          isResearcherGroupList
          gedcomFileList={gedcomFileList}
        />
        {/* Overlay pops up if the URL has a `groupId` and `inviteId` query params */}
        <JoinGroupOverlay onJoinGroup={refetchGroupList} />
      </GroupLimitsProvider>
    </>
  )
}

function ResearchTreeListSkeletons({ isMobile }) {
  return (
    <>
      <LayoutBand marginY="xs">
        <Skeleton.H2 />
        <Skeleton.Paragraph lineCount={2} />
        {/* eslint-disable-next-line test-selectors/button -- loading skeleton */}
        <Skeleton.Button minWidth={160} />
      </LayoutBand>
      <LayoutBand color={colors.gray03} growY marginY="xs">
        <FlowGrid gutters="xs" columnCount={isMobile ? 1 : 2} orientation="vertical">
          {[...new Array(4)].map((d, i) => {
            return (
              // eslint-disable-next-line react/no-array-index-key -- loading skeleton
              <Card key={i} margins="xs">
                <Bleed top start bottom>
                  <Row>
                    <Skeleton.Image width={121} height={91} />
                    <div growX>
                      <Skeleton.H4 />
                      <Skeleton.Chip />
                    </div>
                  </Row>
                </Bleed>
              </Card>
            )
          })}
        </FlowGrid>
      </LayoutBand>
    </>
  )
}
